import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, HelpCircle, Eye, EyeOff, ArrowRight, ArrowLeft, Phone, Mic, Voicemail, Play, Pause, Trash2, MicOff, Upload, Check, Key, Lock, LogOut } from 'lucide-react';
import { Autocomplete, InputBase, CircularProgress, Button, Tooltip } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import axios from 'axios';
import { formatPhoneNumber, getPhoneOptionLabel } from '../utils/formatnumber';
import encryptApi from '../utils/encryptApi';
import { normalizePhoneNumber } from '../utils/actionDetailsLabel';
import Logo from '../reusables/Logo';

const TWILIO_SETUP_VIDEO_URL =
  'https://xq6zcfzg8yfsx411.public.blob.vercel-storage.com/videos/How%20to%20Integrate%20Twilio%20with%20Twilanswer%20-%20May%202026.m4v';

const Onboarding = () => {
  const navigate = useNavigate();

  const handleLogout = () => {
    console.info('[Auth] Logging out: user chose Sign out from onboarding page.');
    localStorage.clear();
    navigate('/login');
  };

  // On load, verify current user still exists (e.g. org may have been deleted for incomplete onboarding)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.userId?.S;
    if (!userId) return;
    axios
      .post(process.env.REACT_APP_BACKEND_URL + "get-user-by-id", { userId })
      .catch(() => {
        // 404 (and other errors) are handled by axios interceptor (clear storage, redirect to login)
      });
  }, []);

  // Check if Twilio is already set up - if so, start on step 2
  const getInitialStep = () => {
    try {
      const organization = JSON.parse(localStorage.getItem("organization"));
      if (organization && organization.twilio_integration) {
        return 2; // Start on Call Action setup if Twilio is already configured
      }
    } catch (error) {
      // Log when org data is missing or corrupt
      console.error('Error reading organization from localStorage:', error);
    }
    return 1; // Default to step 1 (Twilio Setup)
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [videoExpanded, setVideoExpanded] = useState({
    step1: false,
    step2: false
  });
  
  // Step 1: Twilio Information
  const [twilioData, setTwilioData] = useState({
    accountSid: '',
    authToken: '',
    primaryPhoneNumber: '',
    autoconfiguration: true,
  });
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersError, setPhoneNumbersError] = useState(null);
  
  // Step 2: Call Action Setup (currently skipped - conversion test sends users to create-route after Step 1)
  const [callAction, setCallAction] = useState('forwarding'); // 'forwarding' | 'recording' | 'voicemail'
  const [forwardNumber, setForwardNumber] = useState('');
  const [verificationStep, setVerificationStep] = useState('initial'); // 'initial' | 'code' | 'verified'
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationCodeServer, setVerificationCodeServer] = useState(''); // Code we sent via call
  const [verificationError, setVerificationError] = useState(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [recordingMethod, setRecordingMethod] = useState('record'); // 'record' | 'upload'
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingName, setRecordingName] = useState('');
  const [step1Loading, setStep1Loading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioUrlRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const [step1Error, setStep1Error] = useState(null);
  const [finishLoading, setFinishLoading] = useState(false);
  const [finishError, setFinishError] = useState(null);
  
  const handleTwilioInputChange = (field, value) => {
    setTwilioData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Check if both fields are complete after this change
      // Account SID is 34 chars, Auth Token is 32 chars
      if (updated.accountSid.length >= 34 && updated.authToken.length >= 32 && !isRefreshing) {
        // Trigger fetch after state update completes
        setTimeout(async () => {
          setIsRefreshing(true);
          setPhoneNumbersError(null);
          
          try {
            const response = await axios.post(
              process.env.REACT_APP_BACKEND_URL + 'twilio/get-incoming-phone-numbers',
              {
                organization_id: JSON.parse(localStorage.getItem('organization'))?.orgId?.S,
                account_sid: updated.accountSid,
                api_key: await encryptApi(updated.authToken),
              }
            );

            if (response.data.length < 1) {
              setPhoneNumbersError('NO_PHONE_NUMBERS');
              setPhoneNumbers([]);
            } else {
              setPhoneNumbers(response.data);
              setPhoneNumbersError(null);
            }
          } catch (error) {
            // Log when Twilio credentials are invalid
            console.error('Error fetching phone numbers:', error);
            setPhoneNumbersError('Wrong Account SID or Auth Token. Please check your credentials.');
            setPhoneNumbers([]);
            setTwilioData(prevData => ({ ...prevData, primaryPhoneNumber: '' }));
          } finally {
            setIsRefreshing(false);
          }
        }, 100);
      }
      return updated;
    });
  };

  const handleRefreshPhoneNumbers = async () => {
    if (!twilioData.accountSid || !twilioData.authToken) {
      setPhoneNumbersError('Please enter Account SID and Auth Token first');
      return;
    }

    setIsRefreshing(true);
    setPhoneNumbersError(null);
    
    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_URL + 'twilio/get-incoming-phone-numbers',
        {
          organization_id: JSON.parse(localStorage.getItem('organization'))?.orgId?.S,
          account_sid: twilioData.accountSid,
          api_key: await encryptApi(twilioData.authToken),
        }
      );

      if (response.data.length < 1) {
        setPhoneNumbersError('NO_PHONE_NUMBERS');
        setPhoneNumbers([]);
      } else {
        setPhoneNumbers(response.data);
        setPhoneNumbersError(null);
      }
    } catch (error) {
      // Log when Twilio credentials are invalid
      console.error('Error fetching phone numbers:', error);
      setPhoneNumbersError('Wrong Account SID or Auth Token. Please check your credentials.');
      setPhoneNumbers([]);
      setTwilioData(prev => ({ ...prev, primaryPhoneNumber: '' }));
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-fetch phone numbers when Account SID and Auth Token are both filled
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      // Account SID is 34 chars, Auth Token is 32 chars
      if (twilioData.accountSid.length >= 34 && twilioData.authToken.length >= 32 && !isRefreshing) {
        setIsRefreshing(true);
        setPhoneNumbersError(null);
        
        try {
          const response = await axios.post(
            process.env.REACT_APP_BACKEND_URL + 'twilio/get-incoming-phone-numbers',
            {
              organization_id: JSON.parse(localStorage.getItem('organization'))?.orgId?.S,
              account_sid: twilioData.accountSid,
              api_key: await encryptApi(twilioData.authToken),
            }
          );

          if (response.data.length < 1) {
            setPhoneNumbersError('NO_PHONE_NUMBERS');
            setPhoneNumbers([]);
          } else {
            setPhoneNumbers(response.data);
            setPhoneNumbersError(null);
          }
        } catch (error) {
          // Log when Twilio credentials are invalid
          console.error('Error fetching phone numbers:', error);
          setPhoneNumbersError('Wrong Account SID or Auth Token. Please check your credentials.');
          setPhoneNumbers([]);
          setTwilioData(prev => ({ ...prev, primaryPhoneNumber: '' }));
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    fetchPhoneNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twilioData.accountSid, twilioData.authToken]);

  const handleStep1Continue = async () => {
    if (!twilioData.accountSid || !twilioData.authToken || !twilioData.primaryPhoneNumber) return;
    setStep1Error(null);
    setStep1Loading(true);
    try {
      const org = JSON.parse(localStorage.getItem('organization'));
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + 'add-org-twilio_information',
        {
          organization_id: org?.orgId?.S,
          account_sid: twilioData.accountSid,
          api_key: await encryptApi(twilioData.authToken),
          transcription_enabled: false,
          auto_configuration: twilioData.autoconfiguration,
          primary_phone: twilioData.primaryPhoneNumber,
        }
      );
      if (res.data.status === 'success') {
        localStorage.setItem('organization', JSON.stringify(res.data.organization));
        localStorage.setItem('fromOnboarding', 'true');
        // CONVERSION TEST: Skip Step 2 - send users straight to new route page. To re-enable Step 2, change to: setCurrentStep(2);
        window.location.href = '/dashboard/create-route';
      } else {
        setStep1Error('Something went wrong. Please try again.');
      }
    } catch (error) {
      // Log for production diagnostics when saving Twilio credentials fails
      console.error('Onboarding: save Twilio credentials failed', error.response?.data || error.message || error);
      setStep1Error(
        error.response?.data?.message || 'Failed to save Twilio credentials. Please try again.'
      );
    } finally {
      setStep1Loading(false);
    }
  };

  // --- Step 2 code (disabled during conversion test; see handleStep1Continue and currentStep === 2 block) ---
  // Recording helpers (from legacy Register)
  const audioBufferToWav = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const length = buffer.length * numChannels;
    const wavBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(wavBuffer);
    const writeString = (offset, str) => {
      for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
    };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);
    const channelData = buffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < channelData.length; i++) {
      view.setInt16(offset, channelData[i] * 0x7fff, true);
      offset += 2;
    }
    return new Blob([view], { type: 'audio/wav' });
  };

  const resampleAndExport = async (audioBuffer, audioContext) => {
    const offlineCtx = new OfflineAudioContext(1, audioBuffer.length, 8000);
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start(0);
    const resampled = await offlineCtx.startRendering();
    return audioBufferToWav(resampled);
  };

  // Recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const audioContext = new AudioContext({ sampleRate: 8000 });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      const chunks = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = async () => {
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        const rawBlob = new Blob(chunks, { type: 'audio/webm' });
        const arrayBuffer = await rawBlob.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        const processedBlob = await resampleAndExport(audioBuffer, audioContext);
        setAudioBlob(processedBlob);
        audioUrlRef.current = URL.createObjectURL(processedBlob);
        setIsRecording(false);
        setHasRecording(true);
      };
      mediaRecorder.onstart = () => {
        setIsRecording(true);
        setRecordingDuration(0);
        recordingTimerRef.current = setInterval(() => setRecordingDuration(d => d + 1), 1000);
        setTimeout(() => {
          if (mediaRecorder.state === 'recording') {
            if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
            mediaRecorder.stop();
            stream.getTracks().forEach(t => t.stop());
          }
        }, 30000);
      };
      mediaRecorder.start();
    } catch (error) {
      // Log when user denies mic or device unavailable
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaRecorderRef.current) mediaRecorderRef.current.stop();
    if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach(t => t.stop());
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('audio/')) {
      setUploadedFile(file);
      setAudioBlob(null);
      setHasRecording(true);
      setRecordingDuration(Math.floor(Math.random() * 120) + 30);
    } else {
      alert('Please select a valid audio file.');
    }
  };

  const playRecording = () => {
    const url = audioUrlRef.current || (uploadedFile && URL.createObjectURL(uploadedFile));
    if (url) {
      const audio = new Audio(url);
      audio.play();
      setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
    } else {
      setIsPlaying(true);
      setTimeout(() => setIsPlaying(false), 3000);
    }
  };

  const deleteRecording = () => {
    setHasRecording(false);
    setIsPlaying(false);
    setRecordingDuration(0);
    setUploadedFile(null);
    setAudioBlob(null);
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
    audioUrlRef.current = null;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const generateVerificationCode = () => {
    return String(Math.floor(100000 + Math.random() * 900000)); // 6-digit code
  };

  const getTwilioCredentialsForVerification = () => {
    const org = JSON.parse(localStorage.getItem('organization'));
    if (org?.twilio_integration?.M) {
      const t = org.twilio_integration.M;
      return {
        fromNumber: t.primary_phone?.S,
        organization_id: org?.orgId?.S,
      };
    }
    return {
      fromNumber: twilioData.primaryPhoneNumber,
      organization_id: org?.orgId?.S,
    };
  };

  const handleVerifyForwardingNumber = async () => {
    if (!forwardNumber.trim()) return;
    const code = generateVerificationCode();
    setVerificationCodeServer(code);
    setVerificationError(null);
    setVerificationLoading(true);
    setVerificationCode('');

    const forwardingNumberE164 = normalizePhoneNumber(forwardNumber);
    if (!forwardingNumberE164 || forwardingNumberE164.length < 10) {
      setVerificationError('Please enter a valid phone number.');
      setVerificationLoading(false);
      return;
    }

    const creds = getTwilioCredentialsForVerification();
    const orgId = JSON.parse(localStorage.getItem('organization'))?.orgId?.S;
    if (!creds.fromNumber || !orgId) {
      setVerificationError('Twilio credentials are missing. Please complete Step 1 first.');
      setVerificationLoading(false);
      return;
    }

    try {
      await axios.post(
        process.env.REACT_APP_BACKEND_URL + 'twilio/read-num-aloud',
        {
          organization_id: orgId,
          fromNumber: creds.fromNumber,
          forwarding_number: forwardingNumberE164,
          code: await encryptApi(code),
        }
      );
      setVerificationStep('code');
    } catch (error) {
      // Log for production diagnostics when verification call fails
      console.error('Onboarding: verification call failed', error.response?.data || error.message || error);
      setVerificationError(
        error.response?.data?.error || error.response?.data?.message || 'Failed to place verification call. Please try again.'
      );
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleVerificationSubmit = () => {
    setVerificationError(null);
    if (verificationCode === verificationCodeServer) {
      setVerificationStep('verified');
    } else {
      setVerificationError('The verification code does not match. Please try again.');
    }
  };

  // Step 2 handler - only reached when Step 2 is re-enabled
  const handleFinishOnboarding = async () => {
    if (!canContinueStep2()) return;
    setFinishError(null);
    setFinishLoading(true);

    const org = JSON.parse(localStorage.getItem('organization'));
    const user = JSON.parse(localStorage.getItem('user'));
    const orgId = org?.orgId?.S;
    const userEmail = user?.email?.S;

    if (!orgId || !org?.twilio_integration?.M) {
      setFinishError('Missing organization or Twilio setup. Please complete Step 1 first.');
      setFinishLoading(false);
      return;
    }

    try {
      if (callAction === 'forwarding') {
        const forwardNumberE164 = normalizePhoneNumber(forwardNumber);
        await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-channel',
          {
            label: 'Forwarding Number 1',
            last_updated: String(Math.floor(Date.now() / 1000)),
            status: 'active',
            type: 'forward_number',
            value: String(forwardNumberE164 || forwardNumber),
            organization_id: orgId,
            slack_id: '',
            api_key: '',
            front_id: '',
          }
        );

        const emailChannelRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-channel',
          {
            label: 'my email',
            last_updated: String(Math.floor(Date.now() / 1000)),
            status: 'active',
            type: 'email',
            value: userEmail || '',
            organization_id: orgId,
            slack_id: '',
            api_key: '',
            front_id: '',
          }
        );

        const routeRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'route/create-route',
          {
            name: 'Route 1',
            configured_phone: org.twilio_integration.M.primary_phone.S,
            org_id: orgId,
            primaryAction: 'forward',
            primaryRecording: '',
            primary_number: String(forwardNumberE164 || forwardNumber),
            primaryChannel: emailChannelRes.data.channel_id,
            offHoursAction: '',
            offHoursRecording: '',
            offHoursNumber: '',
            offHoursChannel: '',
            calendar: null,
            ivrMenu: null,
            offHoursIvrMenu: null,
          }
        );

        const orgRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-route',
          {
            organization_id: orgId,
            route_id: routeRes.data.route.routeId.S,
          }
        );
        localStorage.setItem('organization', JSON.stringify(orgRes.data.organization));

        const t = JSON.parse(localStorage.getItem('organization')).twilio_integration.M;
        if (t?.auto_configuration?.BOOL) {
          await axios.post(
            process.env.REACT_APP_BACKEND_URL + 'twilio/update-voice-url',
            {
              org_id: orgId,
              phone_number: t.primary_phone.S,
              webhook_url: `https://incoming.twilanswer.com/${routeRes.data.route.routeId.S}`,
            }
          );
        }

        window.location.href = '/dashboard/call-routings';
      } else if (callAction === 'recording' || callAction === 'voicemail') {
        const audioSource = recordingMethod === 'record' ? audioBlob : uploadedFile;
        if (!audioSource) {
          setFinishError('Please record or upload your audio first.');
          setFinishLoading(false);
          return;
        }

        const formData = new FormData();
        const ext = recordingMethod === 'record' ? 'wav' : (uploadedFile?.name?.split('.').pop()?.toLowerCase() || 'mp3');
        formData.append('file', audioSource, `${recordingName}-${Date.now()}.${ext}`);

        const uploadRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'upload',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        const recordType = recordingMethod === 'record' ? 'record_now' : 'upload_mp3';
        const recordRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-recording',
          {
            title: recordingName,
            type: recordType,
            url: uploadRes.data.url,
            organization_id: orgId,
          }
        );

        const emailChannelRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-channel',
          {
            label: 'my email',
            last_updated: String(Math.floor(Date.now() / 1000)),
            status: 'active',
            type: 'email',
            value: userEmail || '',
            organization_id: orgId,
            slack_id: '',
            api_key: '',
            front_id: '',
          }
        );

        const routeRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'route/create-route',
          {
            name: 'Route 1',
            configured_phone: org.twilio_integration.M.primary_phone.S,
            org_id: orgId,
            primaryAction: 'forwardvwm',
            primaryRecording: recordRes.data.rec_id,
            primaryNumber: '',
            primaryChannel: emailChannelRes.data.channel_id,
            offHoursAction: '',
            offHoursRecording: '',
            offHoursNumber: '',
            offHoursChannel: '',
            calendar: null,
            ivrMenu: null,
            offHoursIvrMenu: null,
          }
        );

        const orgRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + 'add-route',
          {
            organization_id: orgId,
            route_id: routeRes.data.route.routeId.S,
          }
        );
        localStorage.setItem('organization', JSON.stringify(orgRes.data.organization));

        const t = JSON.parse(localStorage.getItem('organization')).twilio_integration.M;
        if (t?.auto_configuration?.BOOL) {
          await axios.post(
            process.env.REACT_APP_BACKEND_URL + 'twilio/update-voice-url',
            {
              org_id: orgId,
              phone_number: t.primary_phone.S,
              webhook_url: `https://incoming.twilanswer.com/${routeRes.data.route.routeId.S}`,
            }
          );
        }

        window.location.href = '/dashboard/call-routings';
      }
    } catch (error) {
      // Log for production diagnostics when finish setup fails
      console.error('Onboarding: finish setup failed', error.response?.data || error.message || error);
      setFinishError(
        error.response?.data?.message || error.message || 'Failed to complete setup. Please try again.'
      );
    } finally {
      setFinishLoading(false);
    }
  };

  // Step 2 validation - only used when Step 2 is re-enabled
  const canContinueStep2 = () => {
    if (callAction === 'forwarding') {
      return forwardNumber.trim() !== '' && verificationStep === 'verified';
    }
    if (callAction === 'recording' || callAction === 'voicemail') {
      return hasRecording && recordingName.trim() !== '';
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col py-8 sm:px-6 lg:px-8 relative">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <button
          type="button"
          onClick={handleLogout}
          disabled={step1Loading}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
      {currentStep === 1 ? (
        /* Register-style centered layout for Twilio setup */
        <div className="mx-auto w-full max-w-lg px-4">
          {/* Logo - same as Register */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <Logo size="lg" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 text-center">
            Connect Your Twilio Account
          </h1>
          <p className="text-sm text-gray-600 text-center mt-1" style={{ marginBottom: '1.5rem' }}>
            Don't have a Twilio account?{' '}
            <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-500 font-medium">
              Sign up here
            </a>
          </p>

          {/* Form card - same styling as Register */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8">
            {step1Error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
                {step1Error}
              </div>
            )}

            <button
              onClick={() => setVideoExpanded(prev => ({ ...prev, step1: !prev.step1 }))}
              className="text-sm text-indigo-600 hover:text-indigo-500 font-medium mb-4 inline-flex items-center gap-1"
            >
              <HelpCircle size={14} />
              Watch setup video {videoExpanded.step1 ? '▲' : '▼'}
            </button>

            {videoExpanded.step1 && (
              <div className="mb-6">
                <div className="relative w-full overflow-hidden rounded-lg bg-black" style={{ aspectRatio: '16 / 9' }}>
                  <video
                    className="absolute inset-0 h-full w-full"
                    controls
                    playsInline
                    preload="auto"
                    aria-label="Twilio Setup Tutorial"
                  >
                    <source src={TWILIO_SETUP_VIDEO_URL} type="video/mp4" />
                  </video>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {/* Account SID */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Account SID</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Key size={16} />
                  </div>
                  <InputBase
                    value={twilioData.accountSid}
                    onChange={(e) => handleTwilioInputChange('accountSid', e.target.value)}
                    placeholder="AC1234567890abcdef1234567890abcdef"
                    sx={{
                      bgcolor: '#fff',
                      width: '100%',
                      pl: '36px',
                      pr: 2,
                      py: '6px',
                      fontSize: '14px',
                      borderRadius: '10px',
                      border: '1px solid #D1D5DB',
                      minHeight: 38,
                    }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Found in your <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">Twilio Console</a> dashboard
                </p>
              </div>

              {/* Auth Token */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Auth Token</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Lock size={16} />
                  </div>
                  <InputBase
                    type={showAuthToken ? 'text' : 'password'}
                    value={twilioData.authToken}
                    onChange={(e) => handleTwilioInputChange('authToken', e.target.value)}
                    placeholder="Your Twilio Auth Token"
                    sx={{
                      bgcolor: '#fff',
                      width: '100%',
                      pl: '36px',
                      pr: '36px',
                      py: '6px',
                      fontSize: '14px',
                      borderRadius: '10px',
                      border: '1px solid #D1D5DB',
                      minHeight: 38,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAuthToken(!showAuthToken)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showAuthToken ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Found in your <a href="https://console.twilio.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">Twilio Console</a> (keep this secure!)
                </p>
              </div>

              {/* Primary Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-1">Primary Phone Number</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                      <Phone size={16} />
                    </div>
                    <Autocomplete
                      value={phoneNumbers.find((n) => n.phoneNumber === twilioData.primaryPhoneNumber) || null}
                      onChange={(e, newValue) => {
                        handleTwilioInputChange('primaryPhoneNumber', newValue ? newValue.phoneNumber : '');
                      }}
                      options={phoneNumbers}
                      loading={isRefreshing}
                      getOptionLabel={(option) => getPhoneOptionLabel(option)}
                      renderInput={(params) => (
                        <InputBase
                          {...params.InputProps}
                          inputProps={params.inputProps}
                          placeholder="Search or select a phone number"
                          sx={{
                            bgcolor: '#fff',
                            width: '100%',
                            pl: '36px',
                            pr: '36px',
                            py: '6px',
                            fontSize: '14px',
                            borderRadius: '10px',
                            border: '1px solid #D1D5DB',
                            minHeight: 38,
                          }}
                          endAdornment={
                            <>
                              {isRefreshing ? (
                                <CircularProgress size={18} sx={{ mr: 1 }} />
                              ) : (
                                params.InputProps.endAdornment
                              )}
                            </>
                          }
                        />
                      )}
                      renderOption={(props, option) => (
                          <li {...props}>
                            {getPhoneOptionLabel(option)}
                          </li>
                        )}
                      popupIcon={<KeyboardArrowDown />}
                      disableClearable
                      fullWidth
                      ListboxProps={{ style: { maxHeight: 260 } }}
                    />
                  </div>
                  <button
                    onClick={handleRefreshPhoneNumbers}
                    disabled={isRefreshing || !twilioData.accountSid || !twilioData.authToken}
                    className="flex-shrink-0 p-2 border border-gray-300 rounded-[10px] text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 h-[38px]"
                    title={!twilioData.accountSid || !twilioData.authToken ? 'Enter Account SID and Auth Token first' : 'Refresh phone numbers'}
                  >
                    <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  </button>
                </div>
                {phoneNumbersError && (
                  <p className="mt-1 text-xs text-red-500">
                    {phoneNumbersError === 'NO_PHONE_NUMBERS' ? (
                      <>
                        Your Twilio account does not have any incoming phone numbers.{' '}
                        <a
                          href="https://twilanswer.com/docs/how-to-purchase-a-twilio-phone-number/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-indigo-600 hover:text-indigo-800 underline"
                        >
                          Learn how to purchase a Twilio phone number
                        </a>
                      </>
                    ) : (
                      phoneNumbersError
                    )}
                  </p>
                )}
                {!phoneNumbersError && (
                  <p className="mt-1 text-xs text-gray-500">
                    The main phone number for call forwarding. You can change this later.
                  </p>
                )}
              </div>

              {/* Autoconfiguration */}
              <div className="pt-2">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center">
                    <label className="text-sm font-semibold text-gray-900">Autoconfiguration</label>
                    <HelpCircle size={14} className="text-gray-400 cursor-help ml-1" title="Automatically configure Twilio webhooks" />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleTwilioInputChange('autoconfiguration', !twilioData.autoconfiguration)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      twilioData.autoconfiguration ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                        twilioData.autoconfiguration ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <span className="text-sm text-green-600 font-medium">Recommended</span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  We'll update your Twilio webhook settings automatically, or you can do it manually later.
                </p>
              </div>
            </div>

            {/* Continue Button - same style as Register */}
            <Tooltip
              title={
                (!twilioData.accountSid || !twilioData.authToken || !twilioData.primaryPhoneNumber) && !step1Loading
                  ? 'Enter your Twilio credentials to continue'
                  : ''
              }
              disableHoverListener={
                twilioData.accountSid && twilioData.authToken && twilioData.primaryPhoneNumber
              }
            >
              <span style={{ display: 'block', width: '100%' }}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleStep1Continue}
                  disabled={!twilioData.accountSid || !twilioData.authToken || !twilioData.primaryPhoneNumber || step1Loading}
                  sx={{
                py: '12px',
                borderRadius: '8px',
                fontSize: '15px',
                mt: 3,
                '&:disabled': { bgcolor: 'rgba(96, 91, 255, 0.5)', color: 'white' },
              }}
            >
              {step1Loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Continue'}
            </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      ) : (
        /* CONVERSION TEST: Step 2 disabled - users go to /dashboard/create-route after Step 1.
           To re-enable: change handleStep1Continue redirect to setCurrentStep(2) and remove false && */
        <div className="min-h-screen bg-gray-50">
          {false && currentStep === 2 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className="text-4xl mr-3">⚡</div>
                <h1 className="text-2xl font-bold text-gray-900">Setup Your First Call Action</h1>
              </div>
              <p className="text-gray-600">
                Choose what happens when someone calls your phone number. You can always change this later or add more complex routing.
              </p>
            </div>

            {/* Help Resources */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-900 mb-2">🤔 Need Help?</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#"
                  className="inline-flex items-center text-sm text-blue-700 hover:text-blue-600"
                >
                  <HelpCircle size={16} className="mr-1" />
                  View Call Action Guide
                </a>
                <button
                  onClick={() => setVideoExpanded(prev => ({ ...prev, step2: !prev.step2 }))}
                  className="inline-flex items-center text-sm text-blue-700 hover:text-blue-600"
                >
                  📺 Watch our call action tutorial {videoExpanded.step2 ? '⬆️' : '⬇️'}
                </button>
              </div>
            </div>

            {/* Embedded Video */}
            {videoExpanded.step2 && (
              <div className="mb-8 text-center">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Call Action Setup Tutorial</h4>
                  <button
                    onClick={() => setVideoExpanded(prev => ({ ...prev, step2: false }))}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                  >
                    Minimize ×
                  </button>
                </div>
                <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
                  <iframe
                    src="https://www.youtube.com/embed/Hn1-di--OCI?autoplay=1"
                    title="Call Action Setup Tutorial"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                  ></iframe>
                </div>
              </div>
            )}

            {/* Call Action Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                What should happen when someone calls?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setCallAction('forwarding')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    callAction === 'forwarding'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <Phone className={`h-8 w-8 ${callAction === 'forwarding' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <div>
                      <div className={`font-medium ${callAction === 'forwarding' ? 'text-indigo-900' : 'text-gray-900'}`}>
                        Forward Call
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Forward the call to another phone number (like your mobile or office phone)
                  </p>
                </button>

                <button
                  onClick={() => setCallAction('recording')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    callAction === 'recording'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <Mic className={`h-8 w-8 ${callAction === 'recording' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <div>
                      <div className={`font-medium ${callAction === 'recording' ? 'text-indigo-900' : 'text-gray-900'}`}>
                        Play Recording
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Play a custom message to callers (like business hours or instructions)
                  </p>
                </button>

                <button
                  onClick={() => setCallAction('voicemail')}
                  className={`p-6 rounded-lg border-2 text-left transition-all ${
                    callAction === 'voicemail'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <Voicemail className={`h-8 w-8 ${callAction === 'voicemail' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <div>
                      <div className={`font-medium ${callAction === 'voicemail' ? 'text-indigo-900' : 'text-gray-900'}`}>
                        Voicemail
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Let callers leave a voicemail message after hearing your greeting
                  </p>
                </button>
              </div>
            </div>

            {/* Forwarding Configuration */}
            {callAction === 'forwarding' && (
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Forward to Phone Number</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="tel"
                      value={forwardNumber}
                      onChange={(e) => setForwardNumber(e.target.value)}
                      disabled={verificationStep === 'verified'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="+1 (555) 123-4567"
                    />
                    {verificationStep === 'initial' && (
                      <button
                        onClick={handleVerifyForwardingNumber}
                        disabled={!forwardNumber.trim() || verificationLoading}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {verificationLoading ? (
                          <>
                            <RefreshCw size={16} className="animate-spin mr-2" />
                            Calling...
                          </>
                        ) : (
                          'Verify'
                        )}
                      </button>
                    )}
                    {verificationStep === 'verified' && (
                      <div className="px-4 py-2 bg-green-100 text-green-800 rounded-md flex items-center">
                        <Check size={16} />
                      </div>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the phone number where calls should be forwarded. We'll verify you own this number.
                  </p>
                  {verificationError && (
                    <p className="mt-2 text-sm text-red-600">{verificationError}</p>
                  )}
                  {verificationStep === 'code' && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Verify Your Number</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        We're calling {forwardNumber} right now with a 6-digit verification code. Answer the call and enter the code below to confirm you own this number.
                      </p>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          className="flex-1 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter code from phone call"
                          maxLength={6}
                        />
                        <button
                          onClick={handleVerificationSubmit}
                          disabled={verificationCode.length !== 6}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Verify
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-blue-600">
                        Didn't receive the call?{' '}
                        <button
                          type="button"
                          onClick={handleVerifyForwardingNumber}
                          disabled={verificationLoading}
                          className="underline hover:no-underline disabled:opacity-50"
                        >
                          {verificationLoading ? 'Calling...' : 'Call again'}
                        </button>
                      </p>
                    </div>
                  )}
                  
                  {verificationStep === 'verified' && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center">
                        <Check className="h-5 w-5 text-green-600 mr-2" />
                        <span className="text-sm font-medium text-green-900">
                          Phone number verified successfully!
                        </span>
                      </div>
                      <p className="text-sm text-green-700 mt-1">
                        Calls will be forwarded to {forwardNumber}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recording/Voicemail Configuration */}
            {(callAction === 'recording' || callAction === 'voicemail') && (
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {callAction === 'recording' ? 'Recording Setup' : 'Voicemail Greeting'}
                </h3>
                
                {/* Recording Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recording Name
                  </label>
                  <input
                    type="text"
                    value={recordingName}
                    onChange={(e) => setRecordingName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder={callAction === 'recording' ? 'Business Hours Message' : 'Voicemail Greeting'}
                  />
                </div>

                {/* Recording Method Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    How would you like to create your {callAction === 'recording' ? 'message' : 'greeting'}?
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => setRecordingMethod('record')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        recordingMethod === 'record'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Mic className={`h-5 w-5 ${recordingMethod === 'record' ? 'text-indigo-600' : 'text-gray-400'}`} />
                        <div>
                          <div className={`font-medium ${recordingMethod === 'record' ? 'text-indigo-900' : 'text-gray-900'}`}>
                            Record Live
                          </div>
                          <div className="text-sm text-gray-500">
                            Record using your microphone
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setRecordingMethod('upload')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        recordingMethod === 'upload'
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Upload className={`h-5 w-5 ${recordingMethod === 'upload' ? 'text-indigo-600' : 'text-gray-400'}`} />
                        <div>
                          <div className={`font-medium ${recordingMethod === 'upload' ? 'text-indigo-900' : 'text-gray-900'}`}>
                            Upload File
                          </div>
                          <div className="text-sm text-gray-500">
                            Upload an audio file
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Recording Interface */}
                {recordingMethod === 'record' && (
                  <div className="text-center space-y-4">
                    {!hasRecording ? (
                      <>
                        <div className="flex justify-center">
                          <button
                            onClick={isRecording ? stopRecording : startRecording}
                            className={`p-6 rounded-full transition-all ${
                              isRecording 
                                ? 'bg-red-600 hover:bg-red-700 text-white' 
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                            }`}
                          >
                            {isRecording ? <MicOff size={32} /> : <Mic size={32} />}
                          </button>
                        </div>
                        <div className="text-lg font-mono">
                          {formatDuration(recordingDuration)}
                        </div>
                        <p className="text-sm text-gray-500">
                          {isRecording ? 'Recording... Click to stop' : 'Click to start recording'}
                        </p>
                      </>
                    ) : (
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <div className="flex items-center justify-center space-x-4 mb-4">
                          <button
                            onClick={playRecording}
                            disabled={isPlaying}
                            className="p-3 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                          </button>
                          <div className="text-center">
                            <div className="font-mono text-lg">{formatDuration(recordingDuration)}</div>
                            <div className="text-sm text-gray-500">Duration</div>
                          </div>
                          <button
                            onClick={deleteRecording}
                            className="p-3 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                        {isPlaying && (
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '45%' }}></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Interface */}
                {recordingMethod === 'upload' && (
                  <div className="text-center space-y-4">
                    {!hasRecording ? (
                      <>
                        <input
                          type="file"
                          accept="audio/*"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="audio-upload"
                        />
                        <label
                          htmlFor="audio-upload"
                          className="cursor-pointer w-full p-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors block"
                        >
                          <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Click to upload audio file</p>
                          <p className="text-xs text-gray-500 mt-1">Supports MP3, WAV, M4A files</p>
                        </label>
                      </>
                    ) : (
                      <div className="bg-white rounded-lg p-6 border border-gray-200">
                        <div className="flex items-center justify-center space-x-4 mb-4">
                          <button
                            onClick={playRecording}
                            disabled={isPlaying}
                            className="p-3 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                          </button>
                          <div className="text-center">
                            <div className="font-medium text-gray-900">{uploadedFile?.name}</div>
                            <div className="font-mono text-lg">{formatDuration(recordingDuration)}</div>
                            <div className="text-sm text-gray-500">Duration</div>
                          </div>
                          <button
                            onClick={deleteRecording}
                            className="p-3 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                        {isPlaying && (
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-indigo-600 h-2 rounded-full animate-pulse" style={{ width: '45%' }}></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex flex-col items-stretch gap-2 mt-6">
              {finishError && (
                <p className="text-sm text-red-600">{finishError}</p>
              )}
              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <ArrowLeft size={16} className="mr-2" />
                  Back
                </button>
                <button
                onClick={handleFinishOnboarding}
                disabled={!canContinueStep2() || finishLoading}
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {finishLoading ? (
                  <>
                    <RefreshCw size={20} className="animate-spin mr-2" />
                    Completing...
                  </>
                ) : (
                  <>
                    Complete Setup 🎉
                    <ArrowRight size={20} className="ml-2" />
                  </>
                )}
                </button>
              </div>
            </div>

            {/* Skip option */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-1">Need something more advanced?</p>
              <button
                type="button"
                onClick={() => { window.location.href = '/dashboard/create-route'; }}
                className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
              >
                Skip this step and build your call routing exactly how you want in Call Routes.
              </button>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default Onboarding;
